import React, { Component } from "react";

class ISPClientDialog extends Component {
  modalRef = React.createRef();

  state = {
    firstName: this.props.editData?.firstName || "",
    middleName: this.props.editData?.middleName || "",
    lastName: this.props.editData?.lastName || "",
    isPrimary: this.props.editData?.isPrimary ?? this.props.isFirstClient ?? false,
    // T10 — last 4 of SSN, required only on the primary account holder.
    ssnLast4: this.props.editData?.ssnLast4 || "",
  };

  componentDidMount() {
    // Focus the first input when modal opens
    const firstInput = document.getElementById("dialogFirstName");
    if (firstInput) firstInput.focus();
    // Listen for Escape key
    document.addEventListener("keydown", this.handleEscapeKey);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleEscapeKey);
  }

  handleEscapeKey = (e) => {
    if (e.key === "Escape") {
      this.props.onClose();
    }
  };

  handleChange = (field, value) => {
    this.setState({ [field]: value });
  };

  // Loose split logic for name suggestions
  // Supports current "CLIENT NAMES" format ("John Smith") and future
  // separate firstName/lastName fields from DynamoDB submissions
  getFirstNameSuggestions = () => {
    const history = this.props.clientNameHistory || [];
    const firsts = new Set();
    history.forEach((name) => {
      const parts = name.trim().split(/\s+/);
      if (parts[0]) firsts.add(parts[0]);
    });
    return [...firsts];
  };

  getLastNameSuggestions = () => {
    const history = this.props.clientNameHistory || [];
    const lasts = new Set();
    history.forEach((name) => {
      const parts = name.trim().split(/\s+/);
      if (parts.length > 1) lasts.add(parts[parts.length - 1]);
    });
    return [...lasts];
  };

  isValid = () => {
    if (!this.state.firstName || !this.state.lastName) return false;
    // Primary holder must include last-4 of SSN; co-holders are optional.
    if (this.state.isPrimary && (!this.state.ssnLast4 || this.state.ssnLast4.length < 4)) {
      return false;
    }
    return true;
  };

  handleSave = () => {
    if (!this.isValid()) return;
    this.props.onSave({
      ...this.state,
      // Always normalize: only the primary holder carries an SSN last-4.
      ssnLast4: this.state.isPrimary ? this.state.ssnLast4 : "",
      clientName: `${this.state.firstName} ${this.state.middleName} ${this.state.lastName}`
        .replace(/\s+/g, " ")
        .trim(),
    });
  };

  render() {
    const { onClose, isEditing } = this.props;
    const { firstName, middleName, lastName } = this.state;
    const prefillGeneration = this.props.prefillGeneration || 0;

    return (
      <div>
        {/* Backdrop */}
        <div
          className="modal-backdrop fade show"
          style={{ zIndex: 1050 }}
          onClick={onClose}
        />

        {/* Modal */}
        <div
          className="modal fade show"
          style={{ display: "block", zIndex: 1060 }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ispClientDialogTitle"
          ref={this.modalRef}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered"
          >
            <div
              className="modal-content"
              style={{ maxHeight: "85vh", overflowY: "auto" }}
            >
              <div className="modal-header">
                <h5 className="modal-title" id="ispClientDialogTitle" style={{ color: "#385EA4" }}>
                  {isEditing ? "Edit Client / Account Holder" : "Add Client / Account Holder"}
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div className="modal-body" style={{ padding: "20px 30px" }}>
                {/* Client Name (First + Middle + Last) */}
                {/* Loose split logic: supports both "First Last" and future separate first/last fields from DynamoDB */}
                <div className="form-row">
                  <div className="form-group col-md-5">
                    <label htmlFor="dialogFirstName">
                      <strong>First Name</strong>
                    </label>
                    <input
                      type="text"
                      id="dialogFirstName"
                      name={`isp-client-first-name-v${prefillGeneration}`}
                      list="ispDialogFirstNames"
                      autoComplete="off"
                      className="form-control"
                      style={{
                        borderLeftColor: firstName ? "green" : "#ced4da",
                        borderLeftWidth: "5px",
                      }}
                      value={firstName}
                      onChange={(e) => this.handleChange("firstName", e.target.value)}
                      placeholder="First name"
                      maxLength={200}
                    />
                    <datalist id="ispDialogFirstNames">
                      {this.getFirstNameSuggestions().map((name, index) => (
                        <option value={name} key={index} />
                      ))}
                    </datalist>
                  </div>
                  <div className="form-group col-md-2">
                    <label htmlFor="dialogMiddleName">
                      <strong>M.I.</strong> <small className="text-muted">(optional)</small>
                    </label>
                    <input
                      type="text"
                      id="dialogMiddleName"
                      name={`isp-client-middle-name-v${prefillGeneration}`}
                      autoComplete="off"
                      className="form-control"
                      value={middleName}
                      onChange={(e) => this.handleChange("middleName", e.target.value)}
                      placeholder="M.I."
                      maxLength={1}
                    />
                  </div>
                  <div className="form-group col-md-5">
                    <label htmlFor="dialogLastName">
                      <strong>Last Name</strong>
                    </label>
                    <input
                      type="text"
                      id="dialogLastName"
                      name={`isp-client-last-name-v${prefillGeneration}`}
                      list="ispDialogLastNames"
                      autoComplete="off"
                      className="form-control"
                      style={{
                        borderLeftColor: lastName ? "green" : "#ced4da",
                        borderLeftWidth: "5px",
                      }}
                      value={lastName}
                      onChange={(e) => this.handleChange("lastName", e.target.value)}
                      placeholder="Last name"
                      maxLength={200}
                    />
                    <datalist id="ispDialogLastNames">
                      {this.getLastNameSuggestions().map((name, index) => (
                        <option value={name} key={index} />
                      ))}
                    </datalist>
                  </div>
                </div>
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isPrimary"
                    checked={this.state.isPrimary}
                    onChange={(e) => {
                      const next = e.target.checked;
                      this.setState({
                        isPrimary: next,
                        // Drop the SSN field when this client is no longer
                        // the primary holder so we never persist a captured
                        // SSN we don't actually render or use.
                        ssnLast4: next ? this.state.ssnLast4 : "",
                      });
                    }}
                    style={{ height: "15px", width: "15px" }}
                  />
                  <label className="form-check-label" htmlFor="isPrimary">
                    Primary Account Holder
                  </label>
                </div>

                {/* T10 — primary-only SSN last-4. Required for the primary
                    holder; the field disappears for joint co-holders. */}
                {this.state.isPrimary && (
                  <div className="form-group" style={{ maxWidth: "200px" }}>
                    <label htmlFor="dialogSsnLast4">
                      <strong>Last 4 of SSN</strong>
                    </label>
                    <input
                      type="text"
                      id="dialogSsnLast4"
                      className="form-control"
                      style={{
                        borderLeftColor:
                          this.state.ssnLast4 && this.state.ssnLast4.length === 4
                            ? "green"
                            : "#ced4da",
                        borderLeftWidth: "5px",
                      }}
                      maxLength={4}
                      inputMode="numeric"
                      autoComplete="off"
                      value={this.state.ssnLast4}
                      onChange={(e) =>
                        this.handleChange(
                          "ssnLast4",
                          e.target.value.replace(/[^0-9]/g, "")
                        )
                      }
                      placeholder="####"
                    />
                    <small className="form-text text-muted">
                      Last 4 Digits of Primary Account Holder's SSN
                    </small>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={!this.isValid()}
                  onClick={this.handleSave}
                >
                  {isEditing ? "Update" : "Add Client"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ISPClientDialog;
